import { motion } from "framer-motion";
import { Briefcase } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { DoulaListing } from "./types";
import { birthRanges } from "./types";

interface ProfessionalInformationCardProps {
	listing: DoulaListing | null;
	onListingChange: (field: keyof DoulaListing, value: string | number) => void;
}

export default function ProfessionalInformationCard({
	listing,
	onListingChange,
}: ProfessionalInformationCardProps) {
	return (
		<motion.div
			initial={{ opacity: 0, x: 20 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ delay: 0.2 }}
		>
			<Card className="glass-card border-0 shadow-lg">
				<CardHeader>
					<CardTitle className="text-xl font-karla flex items-center">
						<Briefcase className="h-5 w-5 mr-2 text-newTheme-orange" />
						Professional Information
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<div>
						<Label
							htmlFor="title"
							className="text-sm font-medium text-newTheme-grey font-karla"
						>
							Professional Title
						</Label>
						<Input
							id="title"
							placeholder="e.g., Certified Birth Doula"
							value={listing?.title || ""}
							onChange={(e) => onListingChange("title", e.target.value)}
							className="mt-1"
						/>
					</div>

					<div>
						<Label
							htmlFor="bio"
							className="text-sm font-medium text-newTheme-grey font-karla"
						>
							Professional Bio
						</Label>
						<Textarea
							id="bio"
							placeholder="Tell parents about your approach and philosophy..."
							value={listing?.bio || ""}
							onChange={(e) => onListingChange("bio", e.target.value)}
							rows={4}
							className="mt-1"
						/>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label
								htmlFor="experience"
								className="text-sm font-medium text-newTheme-grey font-karla"
							>
								Years of Experience
							</Label>
							<Input
								id="experience"
								type="number"
								value={listing?.years_experience || 0}
								onChange={(e) =>
									onListingChange(
										"years_experience",
										parseInt(e.target.value) || 0,
									)
								}
								className="mt-1"
							/>
						</div>
						<div>
							<Label
								htmlFor="births"
								className="text-sm font-medium text-newTheme-grey font-karla"
							>
								Births Attended
							</Label>

							<select
								value={listing?.births_attended_range || ""}
								onChange={(e) =>
									onListingChange("births_attended_range", e.target.value)
								}
								name="births"
								id="births"
								className="flex h-10 w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
							>
								<option value="">Select birth range</option>
								{birthRanges.map((range) => (
									<option key={range} value={range}>
										{range} births
									</option>
								))}
							</select>

						</div>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
